import { area as turfArea } from "@turf/turf";
import { ckmeans, min } from "simple-statistics";
import { isEmpty, uniq, clamp } from "lodash";
import { schemeBlues } from "d3-scale-chromatic";

export const SQ_METERS_PER_SQ_MILE = 2589988.110336;
export const DENSITY_UNIT = "trips / sq mi";

/**
 * Zone areas in square miles, keyed by zone id.
 * @param {Object} zones GeoJSON FeatureCollection of the study zones
 * @param {String} idKey zone id property
 * @return {Map<String, Number>}
 */
export function zoneAreas(zones, idKey) {
  const areas = new Map();
  for (const feature of zones?.features || []) {
    if (!feature.geometry) {
      continue;
    }
    areas.set(String(feature.properties[idKey]), turfArea(feature) / SQ_METERS_PER_SQ_MILE);
  }
  return areas;
}

/**
 * Per-zone trip totals from the origin/destination matrix rows. Inbound and outbound
 * both include intra-zonal trips, so `total` (inbound + outbound) matches the totals
 * the choropleth and thresholds have always used.
 * @param {Array<{origin_id, destination_id, total}>} matrix
 * @return {Map<String, { inbound: Number, outbound: Number, intrazonal: Number, total: Number }>}
 */
export function matrixZoneStats(matrix) {
  const stats = new Map();
  const get = (id) => {
    if (!stats.has(id)) {
      stats.set(id, { inbound: 0, outbound: 0, intrazonal: 0, total: 0 });
    }
    return stats.get(id);
  };
  for (const row of matrix || []) {
    const total = +row.total || 0;
    const origin = String(row.origin_id);
    const destination = String(row.destination_id);
    get(origin).outbound += total;
    get(destination).inbound += total;
    if (origin === destination) {
      get(origin).intrazonal += total;
    }
  }
  for (const zone of stats.values()) {
    zone.total = zone.inbound + zone.outbound;
  }
  return stats;
}

/**
 * Trip density (total trips per square mile) for every zone with a known area.
 * @param {Map<String, {total: Number}>} stats from matrixZoneStats
 * @param {Map<String, Number>} areas from zoneAreas
 * @return {Map<String, Number>}
 */
export function zoneDensities(stats, areas) {
  const densities = new Map();
  for (const [zone, area] of areas) {
    if (area > 0) {
      densities.set(zone, (stats.get(zone)?.total || 0) / area);
    }
  }
  return densities;
}

/** Floor a positive number to the given number of significant digits (1234 -> 1200, 0.0456 -> 0.045). */
export function floorToSignificant(value, digits = 2) {
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }
  const scale = 10 ** (Math.floor(Math.log10(value)) - digits + 1);
  return Math.floor(value / scale) * scale;
}

/**
 * Natural-break lower bounds (up to eight ckmeans clusters) rounded down to two
 * significant digits, de-duplicated and ascending so they are valid "step" stops.
 * Unlike the LBS getThresholds query this does not assume whole-hundred trip counts,
 * so it suits densities and per-pair totals as well.
 * @param {Iterable<Number>} values
 * @param {Number} [maxClusters=8]
 * @return {Array<Number>}
 */
export function computeNiceThresholds(values, maxClusters = 8) {
  const data = Array.from(values).filter((v) => Number.isFinite(v));
  if (isEmpty(data)) {
    return [];
  }
  const clusters = ckmeans(data, min([data.length, maxClusters]));
  return uniq(clusters.map((cluster) => floorToSignificant(cluster[0]))).sort((a, b) => a - b);
}

/**
 * Sequential blue ramp for a set of lower-bound thresholds.
 * Values below the first positive stop take the lightest colour; the legend lists
 * every band from "0+" upwards.
 * @param {Array<Number>} thresholds ascending lower bounds
 * @return {{ stops: Array<Number>, colors: Array<String>, legend: Array<{ threshold: Number, color: String }> }}
 */
export function colorRamp(thresholds) {
  const stops = uniq(thresholds.filter((t) => t > 0)).sort((a, b) => a - b);
  const count = stops.length + 1;
  // schemeBlues is only defined for 3..9 colours; take the darkest `count` of the nearest scheme
  const colors = schemeBlues[clamp(count, 3, 9)].slice(-count);
  const legend = [{ threshold: 0, color: colors[0] }, ...stops.map((threshold, index) => ({ threshold, color: colors[index + 1] }))];
  return { stops, colors, legend };
}

/**
 * MapLibre "step" expression colouring `input` with the ramp. With no stops the
 * ramp is a single colour, which "step" cannot express, so the colour itself is returned.
 * @param {Array} input expression yielding a number
 * @param {{ stops: Array<Number>, colors: Array<String> }} ramp from colorRamp
 * @return {Array|String}
 */
export function stepExpression(input, { stops, colors }) {
  if (isEmpty(stops)) {
    return colors[0];
  }
  const expression = ["step", input, colors[0]];
  stops.forEach((stop, index) => expression.push(stop, colors[index + 1]));
  return expression;
}

/**
 * Trips between one zone and every other zone it exchanges trips with.
 * @param {Array<{origin_id, destination_id, total}>} matrix
 * @param {String} zoneId
 * @return {{ zoneId, inbound, outbound, intrazonal, total, pairs: Map<String, {inbound, outbound, total}>, connected: Number }}
 *   pairs: keyed by partner zone id (the zone itself is excluded); inbound = trips from the partner
 *   into this zone, outbound = trips from this zone to the partner
 */
export function connectedZones(matrix, zoneId) {
  const id = String(zoneId);
  const pairs = new Map();
  const pair = (other) => {
    if (!pairs.has(other)) {
      pairs.set(other, { inbound: 0, outbound: 0, total: 0 });
    }
    return pairs.get(other);
  };
  let inbound = 0;
  let outbound = 0;
  let intrazonal = 0;
  for (const row of matrix || []) {
    const origin = String(row.origin_id);
    const destination = String(row.destination_id);
    const total = +row.total || 0;
    if (origin === id && destination === id) {
      intrazonal += total;
      inbound += total;
      outbound += total;
    } else if (origin === id) {
      outbound += total;
      pair(destination).outbound += total;
    } else if (destination === id) {
      inbound += total;
      pair(origin).inbound += total;
    }
  }
  for (const [other, p] of pairs) {
    p.total = p.inbound + p.outbound;
    if (p.total <= 0) {
      pairs.delete(other);
    }
  }
  return { zoneId: id, inbound, outbound, intrazonal, total: inbound + outbound, pairs, connected: pairs.size };
}
